import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

/* Max Customs: pre-checkout "kiosk" offer + add-to-cart celebration */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const OFFER_KEY = 'mc-offer-shown';
let cartProductIds = new Set();
let lastCelebrate = 0;
let bypassOffer = false;

async function refreshCart() {
  try {
    const res = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    cartProductIds = new Set(cart.items.map((i) => String(i.product_id)));
    return cart;
  } catch (e) {
    return null;
  }
}

/* ---------- Pre-checkout offer (kiosk style) ---------- */

const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
const fmt = (cents) =>
  new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(cents / 100);

function getOffer() {
  const d = document.getElementById('mc-offer');
  return d instanceof HTMLDialogElement ? d : null;
}

function offerCards(dialog) {
  return [...dialog.querySelectorAll('.mc-offer__card')];
}

function resetOffer(dialog) {
  offerCards(dialog).forEach((card) => {
    card.hidden = cartProductIds.has(card.getAttribute('data-product-id'));
    setSelected(card, false);
  });
  const err = dialog.querySelector('.mc-offer__error');
  if (err) err.hidden = true;
  const confirm = dialog.querySelector('[data-mc-offer-confirm]');
  if (confirm instanceof HTMLButtonElement) confirm.disabled = false;
  updateFooter(dialog);
}

function setSelected(card, on) {
  card.classList.toggle('is-selected', on);
  card.querySelectorAll('[data-mc-offer-toggle]').forEach((b) => b.setAttribute('aria-pressed', String(on)));
}

function updateFooter(dialog) {
  const selected = offerCards(dialog).filter((c) => !c.hidden && c.classList.contains('is-selected'));
  const total = selected.reduce((sum, c) => sum + Number(c.dataset.price || 0), 0);
  const confirm = dialog.querySelector('[data-mc-offer-confirm]');
  const skip = dialog.querySelector('[data-mc-offer-skip]');
  if (confirm) {
    confirm.textContent = selected.length
      ? `Add ${selected.length} item${selected.length === 1 ? '' : 's'} & checkout  (+${fmt(total)})`
      : 'Continue to checkout';
    confirm.classList.toggle('has-items', selected.length > 0);
  }
  if (skip) skip.hidden = selected.length === 0;
}

function goToCheckout() {
  bypassOffer = true;
  window.location.href = '/checkout';
}

// Intercept checkout clicks (cart drawer + cart page)
document.addEventListener(
  'click',
  (event) => {
    if (bypassOffer) return;
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest('button[name="checkout"], #checkout, a[href$="/checkout"]');
    if (!trigger) return;
    const dialog = getOffer();
    if (!dialog || dialog.contains(trigger)) return;

    let alreadyShown = false;
    try {
      alreadyShown = sessionStorage.getItem(OFFER_KEY) === '1';
    } catch (e) {}
    if (alreadyShown) return;

    resetOffer(dialog);
    if (!offerCards(dialog).some((c) => !c.hidden)) return; // they already have everything

    event.preventDefault();
    event.stopImmediatePropagation();
    try {
      sessionStorage.setItem(OFFER_KEY, '1');
    } catch (e) {}
    dialog.showModal();
    dialog.querySelector('.mc-offer__grid')?.scrollTo({ top: 0 });
  },
  true
);

function bindOffer() {
  const dialog = getOffer();
  if (!dialog) return;

  dialog.addEventListener('click', async (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;

    // Tap on the dimmed backdrop closes it (back to the cart).
    // Check coordinates so taps on empty space inside the popup don't close it.
    if (t === dialog) {
      const r = dialog.getBoundingClientRect();
      const outside =
        event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom;
      if (outside) dialog.close();
      return;
    }

    if (t.closest('[data-mc-offer-close]')) {
      dialog.close();
      return;
    }

    const pill = t.closest('[data-mc-variant]');
    if (pill) {
      const card = pill.closest('.mc-offer__card');
      card.querySelectorAll('[data-mc-variant]').forEach((p) => p.classList.toggle('is-active', p === pill));
      card.dataset.variantId = pill.getAttribute('data-mc-variant');
      card.dataset.price = pill.getAttribute('data-price');
      setSelected(card, true);
      updateFooter(dialog);
      return;
    }

    const toggle = t.closest('[data-mc-offer-toggle]');
    if (toggle) {
      const card = toggle.closest('.mc-offer__card');
      setSelected(card, !card.classList.contains('is-selected'));
      updateFooter(dialog);
      return;
    }

    if (t.closest('[data-mc-offer-skip]')) {
      goToCheckout();
      return;
    }

    const confirm = t.closest('[data-mc-offer-confirm]');
    if (confirm instanceof HTMLButtonElement) {
      const selected = offerCards(dialog).filter((c) => !c.hidden && c.classList.contains('is-selected'));
      if (!selected.length) {
        goToCheckout();
        return;
      }
      confirm.disabled = true;
      confirm.textContent = 'Adding…';
      lastCelebrate = Date.now() + 10000; // don't show the toast, we're leaving for checkout
      try {
        const res = await fetch(Theme.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: selected.map((c) => ({ id: Number(c.dataset.variantId), quantity: 1 })) }),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add items');
        goToCheckout();
      } catch (err) {
        console.error('[mc-offer]', err);
        const box = dialog.querySelector('.mc-offer__error');
        if (box) {
          box.textContent = 'Sorry, one of those couldn’t be added. You can still check out.';
          box.hidden = false;
        }
        confirm.disabled = false;
        updateFooter(dialog);
        const skip = dialog.querySelector('[data-mc-offer-skip]');
        if (skip) skip.hidden = false;
      }
    }
  });
}

/* ---------- Add-to-cart celebration ---------- */

function getToast() {
  let toast = document.getElementById('mc-cart-toast');
  if (toast) return toast;
  toast = document.createElement('div');
  toast.id = 'mc-cart-toast';
  toast.className = 'mc-toast';
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.innerHTML = `
    <span class="mc-toast__check" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="18" height="18"><path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </span>
    <span class="mc-toast__text">
      <strong>Added to cart</strong>
      <span class="mc-toast__count"></span>
    </span>
    <button type="button" class="mc-toast__btn" data-mc-toast-view>View cart</button>
    <button type="button" class="mc-toast__close" data-mc-toast-close aria-label="Close">&times;</button>`;
  document.body.appendChild(toast);

  toast.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (t?.closest('[data-mc-toast-view]')) {
      hideToast();
      const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
      if (trigger instanceof HTMLElement) trigger.click();
      else window.location.href = Theme.routes.cart_url;
    } else if (t?.closest('[data-mc-toast-close]')) {
      hideToast();
    }
  });
  return toast;
}

let toastTimer;
function hideToast() {
  document.getElementById('mc-cart-toast')?.classList.remove('is-visible');
}

function showToast(itemCount) {
  const toast = getToast();
  const count = toast.querySelector('.mc-toast__count');
  if (count) count.textContent = itemCount ? `${itemCount} item${itemCount === 1 ? '' : 's'} in your cart` : '';
  toast.classList.remove('is-visible');
  void toast.offsetWidth;
  toast.classList.add('is-visible');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(hideToast, 4500);
}

function bumpCartIcons() {
  document.querySelectorAll('cart-icon').forEach((icon) => {
    icon.classList.remove('mc-bump');
    void icon.offsetWidth;
    icon.classList.add('mc-bump');
    setTimeout(() => icon.classList.remove('mc-bump'), 1300);
  });
}

function celebrate(itemCount) {
  lastCelebrate = Date.now();
  if (!reducedMotion) bumpCartIcons();
  try {
    navigator.vibrate?.(25);
  } catch (e) {}

  setTimeout(() => {
    const drawerOpen = document.querySelector('.cart-drawer__dialog[open]');
    if (!drawerOpen) showToast(itemCount);
  }, 350);
}

document.addEventListener(ThemeEvents.cartUpdate, async (event) => {
  const data = /** @type {any} */ (event).detail?.data || {};
  if (data.didError) return;

  const isAdd =
    (event instanceof CartAddEvent && data.source !== 'mc-sync') || data.source === 'product-form-component';

  const cart = await refreshCart();
  if (isAdd && lastCelebrate < Date.now()) celebrate(cart?.item_count);
});

// Safety net: catch add-to-cart calls that don't fire theme events (e.g. the Battery Shield Builder)
const nativeFetch = window.fetch.bind(window);
window.fetch = async (input, init) => {
  const res = await nativeFetch(input, init);
  try {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url || '';
    if (/\/cart\/add(\.js)?(\?|$)/.test(url) && res.ok) {
      const startedAt = Date.now();
      setTimeout(async () => {
        if (lastCelebrate >= startedAt) return;
        const cart = await refreshCart();
        celebrate(cart?.item_count);
        document.dispatchEvent(
          new CartUpdateEvent({}, 'mc-sync', { source: 'mc-sync', itemCount: cart?.item_count ?? 0 })
        );
      }, 900);
    }
  } catch (e) {}
  return res;
};

// Returning from checkout via the back button: allow checkout clicks again
window.addEventListener('pageshow', () => {
  bypassOffer = false;
  refreshCart();
});

bindOffer();
refreshCart();
